/*
XOL-67 (RTA redesign v2.1, V1): the words and the small rules of Step 4, the Tower Co decision, in pure functions. No Vue, no store, no
axios. The copy is the v2 UX spec (docs/plans/2026-10-27-xol-67-rta-v2.md, section 2.5, 2.7, 4.4, 4.6; its "Step 3" is Step 4 here, decision 154) over v1's Step 4 (UX v3 sections 4, 5
and 7) and decisions 49, 55, 142; the gates are lib/rtaStage.

Step 4 is Approve only (142): the decision is Pending or Approved, the bottom button is Submit decision (E17, it moves the RTA to Finalize), the
Tower Co can Decline the approved option, and ending the RTA is the separate Reject RTA…. There is no Rejected decision, no Lock and no Close RTA.

  approvedNote(rta, names)       why Approved is disabled in the select, or ''
  rebindNote(approvedNo, savedNo)  "Approving option k discards the Finalize entries saved for option n."
  gateNote(names)                the note a Tower Co user who is not the closer reads
  remarksPleaseNote(names)       the "Please Note" alert over the remarks editor
  clientView(rta, ctx)           the client's read-only Step 4: { heading, icon, tone, when, remarks, line }
  approvedOptionRows(option, ctx)  the rows of the approved option's read-only block (UX spec 2.5 item 1): [{ label, value }]
  actionView(action, ctx)        the bottom button: { type, icon, label, hint, disabled, loading }
  keptEntries(rta)               Finalize entries kept across an Unlock: { at, by } or null
  keptLine(kept, ctx)            the line that says so
  proposeConfirm(ctx)            the Propose Option(s) confirm (decision 143): the Draft options and who is e-mailed (the RTA is in Searching)
  finalizeConfirm(ctx)           the Submit decision confirm (was transitionConfirm)
  declineConfirm(ctx)            the Decline… dialog: required reason, the two outcomes
  rejectRtaConfirm(ctx)          the Reject RTA… dialog: required reason
  unlockCopy(rta, ctx)           the askUnlock options for an RTA decision
  sentence(kind, ctx)            the toast sentences: finalize, decline, reject, unlock
  decisionBody(values, base)     the body of POST /actions/{id}/rta/decision
  sameDecision(a, b)             do two form states say the same thing?
  withDraft(rta, draft)          the RTA as the Step 4 form would make it (the buttons follow what is on screen)

ctx { telco, towerCo, name, reference, optionNo, tcClient, mailPossible, mailOff, unsent, fmtDate, fmtDateTime }

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { optionSummary, tcDecision, isDecisionLocked, isTerminal } from '@/lib/rtaStage'
import { hasRemarksText } from '@/lib/remarksText'

// The Step 4 decision is Approve only (142): there is no Rejected decision. A Rejected value is a 422 `invalid_value` at the API.
export var DECISION_STATUSES = ['Pending', 'Approved']

function present (v) {
  return v !== undefined && v !== null && v !== ''
}

function fmt (fn, value) {
  if (!present(value)) {
    return ''
  }

  return typeof fn === 'function' ? String(fn(value)) : String(value)
}

function names (n) {
  n = n || {}

  return {
    telco: n.telco || 'the client',
    towerCo: n.towerCo || 'Tower Co'
  }
}

// "option 2", or a fallback when the server sent no number.
function optionText (n, fallback) {
  return n ? 'option ' + n : fallback
}

// "Gombak RTA 3 · RTA-000918": the subtitle of the RTA dialogs.
function subtitle (c) {
  var parts = []

  if (c.name) parts.push(c.name)
  if (c.reference) parts.push(c.reference)

  return parts.join(' · ')
}

/* ------------------------------------------------------------------------
   The form
------------------------------------------------------------------------ */

/**
 * Why Approved is disabled in the select (UX v3 section 4), or ''. Without an option the client approved AND submitted there is
 * nothing to approve; with one, a review that changed since it was submitted blocks it (decision 47, API response_changed).
 */
export function approvedNote (rta, n) {
  var w = names(n)
  var s = optionSummary(rta)

  if (!s.approvedOptionNo) {
    return 'Approved needs an option that ' + w.telco + ' approved and submitted.'
  }

  if (s.approvedChangedByParty === 'towerco') {
    // The Tower Co's own unsubmitted change (mirrors actionView): nobody else to wait for.
    return 'Approved is unavailable: the review of ' + optionText(s.approvedOptionNo, 'an option') + ' was changed and not submitted.'
  }

  if (s.approvedChangedByParty !== null) {
    return 'Approved is unavailable: ' + w.telco + ' changed its review of ' + optionText(s.approvedOptionNo, 'an option') + ' and hasn\'t submitted it.'
  }

  return ''
}

/**
 * The rebinding note (UX v3 section 4): picking Approved would bind option `approvedNo` while Finalize holds entries for option
 * `savedNo`. A number the screen could not read is left out of the sentence, never guessed.
 */
export function rebindNote (approvedNo, savedNo) {
  return 'Approving ' + (approvedNo ? 'option ' + approvedNo : 'this option') + ' discards the Finalize entries saved for ' + (savedNo ? 'option ' + savedNo : 'another option') + '.'
}

/** What a Tower Co user who is not the closer reads beside the bold decision. */
export function gateNote (n) {
  return 'Only a project, finance or admin manager can change the ' + names(n).towerCo + ' decision.'
}

/** The "Please Note" alert over the remarks editor. */
export function remarksPleaseNote (n) {
  return 'These remarks will be visible to ' + names(n).telco + ' and will be sent in the decision e-mail.'
}

/* ------------------------------------------------------------------------
   The approved option's block (UX spec 2.5 item 1)
------------------------------------------------------------------------ */

function blank (v) {
  return v === undefined || v === null || String(v).trim() === ''
}

// A whole-metre figure that is really there: 0 is no width, as an option form never saves a 0 (a cleared field is empty).
function metres (v) {
  var n = Number(v)

  return blank(v) || isNaN(n) || n <= 0 ? '' : String(n)
}

function point (v) {
  var n = Number(v)

  return blank(v) || isNaN(n) || n === 0 ? '' : n.toFixed(6)
}

/**
 * The rows of the approved option's read-only block: [{ label, value }], value '' when the option has none (the block says "Not entered").
 *   Option, Coordinates, Locality, Structure ("Monopole, 30 m"), Site width × depth ("10 × 10 m"), Status on handover, Land Type,
 *   Landowner (not for the client), Approved ("by Telco Beta on 12 Oct 2026").
 * ctx { structure (the structure type's label, looked up by the caller in the config's tree), telco, towerCo, client (true: no Landowner), fmtDate,
 * optionNo (the number to show when the option row itself could not be read) }
 */
export function approvedOptionRows (option, ctx) {
  var o = option || {}
  var c = ctx || {}
  var w = names(c)
  var lat = point(o.actualLat)
  var lng = point(o.actualLng)
  var height = blank(o.structureHeight) ? '' : o.structureHeight + ' m'
  var width = metres(o.width)
  var depth = metres(o.depth)
  var no = blank(o.optionNo) ? (c.optionNo || '') : o.optionNo
  var when = fmt(c.fmtDate, o.submitted_at || o.client_status_at)
  var byTowerCo = o.submitted_by_party === 'towerco' || (!o.submitted_by_party && o.client_status_by_party === 'towerco')
  var rows = []

  rows.push({ label: 'Option', value: blank(no) ? '' : no + (o.title ? ': ' + o.title : '') })
  rows.push({ label: 'Coordinates', value: lat && lng ? lat + ', ' + lng : '' })
  rows.push({ label: 'Locality', value: [o.district, o.state].filter(function (v) { return !blank(v) }).join(', ') })
  rows.push({ label: 'Structure', value: [c.structure, height].filter(function (v) { return !blank(v) }).join(', ') })
  rows.push({ label: 'Site width × depth', value: width || depth ? (width || '—') + ' × ' + (depth || '—') + ' m' : '' })
  rows.push({ label: 'Status on handover', value: blank(o.handoverStatus) ? '' : String(o.handoverStatus) })
  rows.push({ label: 'Land Type', value: blank(o.landType) ? '' : String(o.landType) })

  if (!c.client) {
    rows.push({ label: 'Landowner', value: blank(o.owner) ? '' : String(o.owner) })
  }

  rows.push({ label: 'Approved', value: when ? 'by ' + (byTowerCo ? w.towerCo : w.telco) + ' on ' + when : '' })

  return rows
}

/* ------------------------------------------------------------------------
   The client's Step 4
------------------------------------------------------------------------ */

/**
 * The client's read-only Step 4 (UX spec 2.5.4): { heading, icon, tone, decided, when, remarks, line, locked }.
 *   heading  "Pending Decision" | "Approved"
 *   line     "You can still change your review of option {n} from the option until {Tower Co} submits its decision." while the RTA is
 *            in Client Approved, "Locked by {Tower Co}." once locked, '' on a closed RTA
 */
export function clientView (rta, ctx) {
  var c = ctx || {}
  var w = names(c)
  var tc = tcDecision(rta)
  var decided = tc.approvedStatus === 'Approved'
  var locked = isDecisionLocked(rta)
  var n = optionSummary(rta).approvedOptionNo || optionSummary(rta).finalOptionNo
  var line = ''

  if (!isTerminal(rta)) {
    line = locked
      ? 'Locked by ' + w.towerCo + '.'
      : (rta && rta.status === 'Client Approved'
        ? 'You can still change your review of ' + optionText(n, 'the approved option') + ' from the option until ' + w.towerCo + ' submits its decision.'
        : '')
  }

  return {
    heading: decided ? 'Approved' : 'Pending Decision',
    icon: decided ? 'fa-check-circle' : 'fa-info-circle',
    tone: decided ? 'text-success' : 'text-muted',
    decided: decided,
    when: decided && tc.approved_at ? 'on ' + fmt(c.fmtDate, tc.approved_at) : '',
    remarks: decided && hasRemarksText(tc.remarks) ? tc.remarks : '',
    line: line,
    locked: locked
  }
}

/* ------------------------------------------------------------------------
   The bottom button, Submit decision (decision 45 as changed; UX spec 4.4)
------------------------------------------------------------------------ */

/**
 * The bottom button as the screen draws it. `action` is rtaStage.step3Action; `busy` is a flush or the command in flight.
 *
 *   ready                      primary, fa-lock, "Submit decision"   "Locks the decision and opens Step 5, Finalize."
 *   Pending                    disabled   "Set the decision to Approved first."
 *   changed review             disabled   "{Telco} changed its review of option {k} and hasn't submitted it. It must submit or undo the change first."
 *                              (the Tower Co's own change, ctx.changedByParty 'towerco': "Option {k}'s review was changed and not submitted.
 *                              Submit or undo it in the option first.") Pass the APPROVED option's number as ctx.changedOptionNo and
 *                              the summary's `approvedChangedByParty` as ctx.changedByParty (P-S1).
 *   not the closer             disabled   "Only a project, finance or admin manager can submit the decision."
 *   busy                       :loading, "Submitting…"
 */
export function actionView (action, ctx) {
  var c = ctx || {}
  var w = names(c)
  var a = action || {}
  var out = {
    type: 'primary',
    icon: 'fa fa-lock',
    label: 'Submit decision',
    hint: '',
    disabled: !a.enabled,
    loading: false
  }

  if (c.busy) {
    out.label = 'Submitting…'
    out.loading = true
    out.disabled = true

    return out
  }

  if (a.enabled) {
    out.hint = 'Locks the decision and opens Step 5, Finalize.'

    return out
  }

  switch (a.reason) {
    case 'pending':
      out.hint = 'Set the decision to Approved first.'
      break
    case 'response_changed':
      out.hint = c.changedByParty === 'towerco'
        ? (c.changedOptionNo ? 'Option ' + c.changedOptionNo + '\'s review' : 'The review') + ' was changed and not submitted. Submit or undo it in the option first.'
        : w.telco + ' changed its review of ' + optionText(c.changedOptionNo, 'an option') + ' and hasn\'t submitted it. It must submit or undo the change first.'
      break
    case 'not_closer':
      out.hint = 'Only a project, finance or admin manager can submit the decision.'
      break
  }

  return out
}

/**
 * Finalize entries kept across an Unlock (decision 46): { at, by } when the RTA carries saved entries (Tower Co callers get
 * `completion`), else null. "Saved entries": a version above 0, or any value in the overlay.
 */
export function keptEntries (rta) {
  var c = rta && rta.completion

  if (!c || typeof c !== 'object') {
    return null
  }

  var values = c.values && typeof c.values === 'object' ? c.values : {}
  var has = Object.keys(values).some(function (k) {
    var v = values[k]

    return v && typeof v === 'object' ? Object.keys(v).length > 0 : present(v)
  })

  if (!has && !(Number(c.version) > 0)) {
    return null
  }

  return { at: c.saved_at || null, by: c.saved_by || null }
}

/** "Finalize entries saved on {date} by {name} are kept for the next Submit decision." */
export function keptLine (kept, ctx) {
  if (!kept) {
    return ''
  }

  var c = ctx || {}
  var when = fmt(c.fmtDate, kept.at)

  return 'Finalize entries saved' + (when ? ' on ' + when : '') + (kept.by ? ' by ' + kept.by : '') + ' are kept for the next Submit decision.'
}

/* ------------------------------------------------------------------------
   The dialogs (UX spec 4.6): one shell, RtaDialog
------------------------------------------------------------------------ */

function unsentWarning (ctx, description) {
  var w = names(ctx)

  return { title: w.telco + ' hasn\'t been e-mailed this decision.', description: description }
}

/**
 * Submit decision: { icon, title, subtitle, okText, okType, paragraphs, warning }. `ctx.unsent` is rtaStage.decisionUnsent
 * (decision 49); the warning is drawn only then.
 */
export function finalizeConfirm (ctx) {
  var c = ctx || {}
  var w = names(c)

  return {
    icon: 'fa-lock',
    title: 'Submit the decision?',
    subtitle: subtitle(c),
    okText: 'Submit decision',
    okType: 'primary',
    paragraphs: [
      'The decision locks, and the reviews freeze until someone unlocks it.',
      c.tcClient ? 'Next, fill in the new site in Step 5.' : 'Next, fill in the new site and the RTS in Step 5.',
      'Nothing is created until you press Complete RTA.'
    ],
    warning: c.unsent ? unsentWarning(c, 'Submit decision sends no e-mail. ' + w.telco + ' is e-mailed when the RTA is completed.') : null
  }
}

// The e-mail sentence of a dialog that writes to the client: only when it is known whether anything goes (mailPossible / mailOff).
function mailParagraph (c, w, goes, off) {
  if (c.tcClient) {
    return null
  }

  if (c.mailOff === true) {
    return off
  }

  return c.mailPossible === true ? goes : null
}

/**
 * Propose Option(s) (decision 143, UX spec 4.6, mock M-7g): an RtaDialog descriptor
 * { icon, title, subtitle, okText, okType, lead, items, meta } (`meta` is the info-meta rows; `items` the change-list).
 *
 *   title    "Propose the options to {Telco}?" / "Propose the options to {Tower Co}?" (the client proposing) / "Propose the options?" (a Tower Co client)
 *   lead     "These options go to {side} for review:" / "These options move to Client Review:" (a Tower Co client)
 *   items    one per Draft option, by number: { optionNo, title, text } with text "Option 1: Roadside lot, Jalan Gombak" ("Option 1" without a title)
 *   meta     one row "E-mailed": "{Telco}, with every option in detail." (the Tower Co proposing) · "{Tower Co}, with a short note and the link."
 *            (the client proposing) · "Nobody: Send E-mails is off for this RTA." · "Nobody: no e-mail is sent for an RTA with your own
 *            organisation as client." (the last two win, in that order, over who proposes). No row when it is not known whether mail goes.
 *            The FIRST proposal of the RTA (U-22, ctx.firstProposal) adds a second row, "Request": "After this, only a {Tower Co} manager can change
 *            the request (Step 1)." (decision 157: the first proposal locks the request to Tower Co managers, for good).
 *
 * ctx { name, reference, target ('client' | 'towerco' | 'self', rtaStage.proposeTarget), options (the Draft option rows: { optionNo, title }),
 * mailPossible (true only when an e-mail would go), mailOff (true when Send E-mails is known off), firstProposal (true while nothing was ever proposed:
 * !rtaStage.hasBeenProposed), telco, towerCo }
 */
export function proposeConfirm (ctx) {
  var c = ctx || {}
  var w = names(c)
  // The sentences here run on ("Propose the options to the Tower Co?"), so the fallback names the party with its article.
  var tcName = c.towerCo || 'the Tower Co'
  var self = c.target === 'self'
  var side = c.target === 'towerco' ? tcName : w.telco
  var rows = (Array.isArray(c.options) ? c.options : []).filter(function (o) { return !!o })
  var value = null

  rows = rows.slice().sort(function (a, b) { return Number(a.optionNo) - Number(b.optionNo) })

  if (self) {
    value = 'Nobody: no e-mail is sent for an RTA with your own organisation as client.'
  } else if (c.mailOff === true) {
    value = 'Nobody: Send E-mails is off for this RTA.'
  } else if (c.mailPossible === true) {
    value = c.target === 'towerco' ? tcName + ', with a short note and the link.' : w.telco + ', with every option in detail.'
  }

  return {
    icon: 'fa-paper-plane',
    title: self ? 'Propose the options?' : 'Propose the options to ' + side + '?',
    subtitle: subtitle(c),
    okText: 'Propose',
    okType: 'success',
    lead: self ? 'These options move to Client Review:' : 'These options go to ' + side + ' for review:',
    items: rows.map(function (o) {
      var label = 'Option ' + o.optionNo

      return { optionNo: o.optionNo, title: present(o.title) ? String(o.title) : '', text: present(o.title) ? label + ': ' + o.title : label }
    }),
    meta: (value === null ? [] : [{ label: 'E-mailed', value: value }]).concat(c.firstProposal === true
      ? [{ label: 'Request', value: 'After this, only a ' + (c.towerCo || 'Tower Co') + ' manager can change the request (Step 1).' }]
      : [])
  }
}

/**
 * Decline… on the approved option (142, I-5): { icon, title, subtitle, okText, okType, paragraphs, reasonLabel, reasonRequired }.
 * `ctx.toStage` is where the RTA goes (rtaStage.stageAfterApprovalLeaves: 'Client Review' while another option is in Review, else 'Searching');
 * `ctx.stillInReviewNo` names the option that is still in review, when known. The reason is required, and the client is e-mailed with it
 * (not for a Tower Co client, and not with Send E-mails off).
 */
export function declineConfirm (ctx) {
  var c = ctx || {}
  var w = names(c)
  var n = c.optionNo ? 'option ' + c.optionNo : 'the option'
  var outcome = c.toStage === 'Searching'
    ? 'The RTA goes back to Searching: every other option is declined.'
    : 'The RTA goes back to Client Review: ' + (c.stillInReviewNo ? 'option ' + c.stillInReviewNo : 'another option') + ' is still in review.'
  var paragraphs = [
    (c.optionNo ? 'Option ' + c.optionNo : 'The option') + ' becomes Declined and your saved decision is cleared.',
    outcome
  ]
  var mail = mailParagraph(c, w, w.telco + ' is e-mailed with your reason.', 'Send E-mails is off, so ' + w.telco + ' isn\'t e-mailed.')

  if (mail) {
    paragraphs.push(mail)
  }

  return {
    icon: 'fa-times-circle',
    title: 'Decline ' + n + '?',
    subtitle: subtitle(c),
    okText: 'Decline ' + n,
    okType: 'danger',
    paragraphs: paragraphs,
    reasonLabel: 'Reason',
    reasonRequired: 'Enter a reason to decline this option.'
  }
}

/**
 * Reject RTA… (142, I-2, I-17): { icon, title, subtitle, okText, okType, paragraphs, reasonLabel, reasonRequired }. One dialog with a
 * required reason; confirming closes the RTA as Rejected at once. The client is e-mailed with the reason (not for a Tower Co client,
 * and not with Send E-mails off).
 */
export function rejectRtaConfirm (ctx) {
  var c = ctx || {}
  var w = names(c)
  var of = c.name ? c.name + (c.reference ? ' (' + c.reference + ')' : '') : (c.reference || 'This RTA')
  var paragraphs = [of + ' closes as Rejected. Every open option becomes Not Selected, and nobody can change the RTA after this.']
  var mail = mailParagraph(c, w, w.telco + ' is e-mailed with your reason.', 'Send E-mails is off, so ' + w.telco + ' isn\'t e-mailed.')

  if (mail) {
    paragraphs.push(mail)
  }

  return {
    icon: 'fa-ban',
    title: 'Reject this RTA?',
    subtitle: subtitle(c),
    okText: 'Reject RTA',
    okType: 'danger',
    paragraphs: paragraphs,
    reasonLabel: 'Reason',
    reasonRequired: 'Enter a reason to reject this RTA.'
  }
}

/**
 * The askUnlock options for an RTA decision (UX v3 section 5): the subject replaces "Step n: title". From Finalize (the only place
 * an approval is locked now) one more sentence says the RTA returns to Step 4 and the Finalize entries are kept.
 */
export function unlockCopy (rta, ctx) {
  var c = ctx || {}
  var w = names(c)
  var tc = tcDecision(rta)
  var n = optionSummary(rta).approvedOptionNo

  return {
    subject: (c.reference || (rta ? 'RTA #' + rta.id : 'RTA')) + ': ' + w.towerCo + ' decision',
    decision: tc.approvedStatus || 'Pending',
    what: 'approval',
    fields: 'the decision, its date, its remarks and ' + w.telco + '\'s option reviews',
    note: rta && rta.status === 'Finalize'
      ? 'The RTA returns to Step 4. ' + w.telco + ' can change its reviews again, and a change to ' + (n ? 'option ' + n + '\'s' : 'the approved option\'s') +
        ' review clears the decision. Your Finalize entries are kept.'
      : ''
  }
}

/* ------------------------------------------------------------------------
   Toasts (UX spec 4.6)
------------------------------------------------------------------------ */

/**
 * The sentence of a command that went through. 'finalize' | 'decline' | 'reject' | 'unlock'.
 * The reject sentence is followed by the response's `notice` (rtaToast); the others carry none.
 *   finalize  Decision submitted. Fill in the new site in Step 5.
 *   decline   Option 2 declined. The RTA is back in Client Review. / ... in Searching.   (ctx.optionNo, ctx.toStage)
 *   reject    RTA rejected.
 *   unlock    Approval unlocked. The RTA is back at Step 4, and your Finalize entries are kept.
 */
export function sentence (kind, ctx) {
  var c = ctx || {}

  switch (kind) {
    case 'finalize':
      return 'Decision submitted. Fill in the new site in Step 5.'
    case 'decline':
      return (c.optionNo ? 'Option ' + c.optionNo : 'The option') + ' declined. The RTA is back in ' + (c.toStage === 'Searching' ? 'Searching' : 'Client Review') + '.'
    case 'reject':
      return 'RTA rejected.'
    case 'unlock':
      return 'Approval unlocked. The RTA is back at Step 4, and your Finalize entries are kept.'
  }

  return ''
}

/* ------------------------------------------------------------------------
   The auto-save body
------------------------------------------------------------------------ */

/**
 * The body of POST /actions/{id}/rta/decision (API-10, E14), from the form `values` { status, date, dateTouched, remarks } and the
 * `base` the user saw ('Approved' | null for Pending):
 *
 *   approvedStatus          'Pending' | 'Approved'  (anything else goes as Pending: the Step 4 decision is Approve only)
 *   expectedApprovedStatus  the base, null for Pending (a stale base is 409 stale_decision)
 *   remarks                 as typed (Pending keeps them, DEV-14)
 *   approved_at             only for Approved, and only when the status did not change OR the user picked the date: a
 *                           status change lets the server stamp today, while a remarks-only change must not move the decision date
 */
export function decisionBody (values, base) {
  var v = values || {}
  var status = DECISION_STATUSES.indexOf(v.status) === -1 ? 'Pending' : v.status
  var body = {
    approvedStatus: status,
    expectedApprovedStatus: base === 'Approved' ? base : null,
    remarks: present(v.remarks) ? v.remarks : null
  }
  var changedStatus = status !== (body.expectedApprovedStatus || 'Pending')

  if (status !== 'Pending' && v.date && (v.dateTouched || !changedStatus)) {
    body.approved_at = v.date
  }

  return body
}

/** Do two form states say the same thing (the status, the day, the remarks)? */
export function sameDecision (a, b) {
  if (!a || !b) {
    return false
  }

  return a.status === b.status && (a.date || null) === (b.date || null) && (a.remarks || '') === (b.remarks || '')
}

/**
 * The RTA as the Step 4 form on screen would make it: `draft` { status, remarks } over the stored decision. The e-mail button and the
 * bottom button follow what the user sees decided, because pressing either flushes the auto-save first and judges again on what the
 * server then holds (so a draft that is refused never sends or submits anything). A copy: the RTA itself is never touched. Without a
 * draft it is the RTA.
 */
export function withDraft (rta, draft) {
  if (!rta || !draft) {
    return rta
  }

  var state = rta.state && typeof rta.state === 'object' ? rta.state : {}
  var rtaState = state.rta && typeof state.rta === 'object' ? state.rta : {}
  var tc = Object.assign({}, rtaState.tc || {}, {
    approvedStatus: draft.status === 'Approved' ? draft.status : null,
    remarks: present(draft.remarks) ? draft.remarks : null
  })

  return Object.assign({}, rta, {
    state: Object.assign({}, state, { rta: Object.assign({}, rtaState, { tc: tc }) })
  })
}
